import { z } from 'zod';
import { CHILLDOC_IMAGE_ID, CHILLDOC_IMAGE_TYPES, CHILLDOC_MAX_IMAGE_BYTES, DOCUMENT_FORMATS, readChilldoc, } from './chilldoc.js';
import { MAX_FILE_BYTES, mimeTypeForFilename } from './file.js';
/**
 * Trang "Tệp" — trình quản lý file của mỗi người (APM-MYF, docs/plan-my-files.md).
 *
 * Trang chỉ liệt kê và cấp URL; nội dung file vẫn đi thẳng giữa trình duyệt và
 * S3 bằng presigned URL (luật #3).
 */
/**
 * Thư mục ảo — suy ra từ liên kết **đã có** của hàng `file_object`, theo đúng
 * thứ tự ưu tiên này (plan §2.1): một file chỉ thuộc một nguồn.
 *
 * 1. `todo` — `todo_id` khác NULL
 * 2. `task` — `task_id` khác NULL
 * 3. `document` — có `document_version` trỏ tới
 * 4. `bug` — có `bug_attachment` trỏ tới (kèm link ngoài của bằng chứng lỗi)
 * 5. `mine` — `project_id` NULL, không thuộc 1–4
 * 6. `project` — có `project_id`, không thuộc 1–4
 */
export const MY_FILE_SOURCES = ['todo', 'task', 'document', 'bug', 'mine', 'project'];
export const MyFileSource = z.enum(MY_FILE_SOURCES);
export const FILE_TYPE_GROUPS = [
    'image',
    'video',
    'audio',
    'document',
    'spreadsheet',
    'presentation',
    'pdf',
    'code',
    'text',
    'archive',
    'other',
];
export const FileTypeGroup = z.enum(FILE_TYPE_GROUPS);
/*
  Đuôi → nhóm. `csv` về `spreadsheet` vì nó xem bằng cùng lưới với xlsx
  (plan §2.6); `md` về `text` — "văn bản" của yêu cầu gốc (plan §1 mục 6).
*/
const EXTENSION_GROUPS = Object.fromEntries([
    ['image', 'png jpg jpeg gif webp svg bmp ico avif heic heif tif tiff'],
    ['video', 'mp4 webm mov m4v mkv avi ogv 3gp'],
    ['audio', 'mp3 wav ogg oga m4a aac flac opus weba'],
    ['document', 'doc docx odt rtf chilldoc'],
    ['spreadsheet', 'xls xlsx ods csv tsv'],
    ['presentation', 'ppt pptx odp'],
    ['pdf', 'pdf'],
    [
        'code',
        'js jsx mjs cjs ts tsx json jsonc yaml yml xml html htm css scss sass less py java kt ' +
            'kts go rs rb php c h cc cpp hpp cs swift sh bash zsh ps1 sql toml ini vue svelte ' +
            'dart lua r pl gradle graphql proto env',
    ],
    ['text', 'txt log md markdown'],
    ['archive', 'zip rar 7z tar gz tgz bz2 xz'],
].flatMap(([group, extensions]) => extensions.split(' ').map((extension) => [extension, group])));
/** MIME → nhóm, chỉ dùng khi đuôi không nói gì. Khớp tiền tố trước, rồi khớp đúng. */
const MIME_GROUPS = [
    [/^image\//, 'image'],
    [/^video\//, 'video'],
    [/^audio\//, 'audio'],
    [/^application\/pdf$/, 'pdf'],
    [
        /^application\/(msword|vnd\.openxmlformats-officedocument\.wordprocessingml|vnd\.oasis\.opendocument\.text|rtf)/,
        'document',
    ],
    [
        /^(application\/(vnd\.ms-excel|vnd\.openxmlformats-officedocument\.spreadsheetml|vnd\.oasis\.opendocument\.spreadsheet)|text\/(csv|tab-separated-values))/,
        'spreadsheet',
    ],
    [
        /^application\/(vnd\.ms-powerpoint|vnd\.openxmlformats-officedocument\.presentationml|vnd\.oasis\.opendocument\.presentation)/,
        'presentation',
    ],
    [
        /^application\/(zip|x-zip-compressed|x-7z-compressed|x-rar-compressed|vnd\.rar|gzip|x-gzip|x-tar|x-bzip2|x-xz)$/,
        'archive',
    ],
    [
        /^(text\/(html|css|javascript|xml|x-[\w.+-]+)|application\/(json|javascript|xml|x-sh|x-yaml|yaml|sql|typescript))$/,
        'code',
    ],
    [/^text\//, 'text'],
];
/**
 * Nhóm loại của một file — dùng chung FE↔BE: server lọc `type`, web chọn
 * biểu tượng và trình xem.
 *
 * Đuôi file **trước**, `mimeType` sau: file cá nhân tải lên tuỳ định dạng có
 * thể mang `application/octet-stream` (plan §2.3, §2.6), còn đuôi thì luôn có
 * ở tên người dùng thấy. Không nhận ra gì ⇒ `other`, không bao giờ ném.
 */
export function fileTypeGroupOf(name, mimeType) {
    const dot = name.lastIndexOf('.');
    // `dot < 1`: không có đuôi, hoặc tên kiểu `.gitignore` (chấm ở đầu không phải đuôi).
    if (dot >= 1) {
        const byExtension = EXTENSION_GROUPS[name.slice(dot + 1).toLowerCase()];
        if (byExtension)
            return byExtension;
    }
    const mime = (mimeType ?? '').toLowerCase().split(';')[0].trim();
    if (mime) {
        for (const [pattern, group] of MIME_GROUPS)
            if (pattern.test(mime))
                return group;
    }
    return 'other';
}
export const MyFileItem = z.object({
    /** `link` = link ngoài của bằng chứng lỗi (`bug_attachment.url`), không có file. */
    kind: z.enum(['file', 'link']),
    /** Id file, hoặc id `bug_attachment` khi `kind = 'link'`. */
    id: z.uuid(),
    /** Tên file, hoặc chú thích (không có thì chính URL) của link. */
    name: z.string(),
    mimeType: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    /** Lúc tải lên xong (ISO). */
    createdAt: z.iso.datetime(),
    source: MyFileSource,
    /** Để dẫn về chỗ gốc. */
    location: z.object({
        projectId: z.uuid().nullable(),
        projectName: z.string().nullable(),
        /** `PRJ-12 · Tên việc`, `Tên tài liệu · v3`, `#7 · Tên lỗi`, tên to-do. */
        label: z.string().nullable(),
        /** Đường dẫn theo router web; `null` với `mine` và khi chỗ gốc đã bị xoá. */
        href: z.string().nullable(),
    }),
    /** Chỉ với `kind = 'link'`. */
    url: z.string().nullable(),
    /** Tính ở server theo luật xoá của plan §3.3. */
    canDelete: z.boolean(),
    /** `true` ⇔ nguồn `mine` và tên kết thúc bằng `.md` (plan §2.4). */
    canEdit: z.boolean(),
    /** Thư mục tự tạo đang chứa tệp (plan §5.3); `null` = gốc hoặc không phải nguồn `mine`. */
    folderId: z.uuid().nullable(),
    /** `true` ⇔ nguồn `mine` — chỉ những tệp này vào được thư mục tự tạo. */
    canMove: z.boolean(),
    /**
     * Số phiên bản của tài liệu (APM-MYF-n), ≥ 1. Trang "Tệp" chỉ liệt kê bản mới nhất; con
     * số này quyết số bóng sau biểu tượng (`versionShadows`).
     */
    versions: z.number().int().min(1),
});
/** Tối đa ký tự ghi chú của một phiên bản (APM-MYF-n) — khớp `file_object.version_note`. */
export const MY_FILE_VERSION_NOTE_MAX = 500;
/**
 * "Lưu thành phiên bản khác" — APM-MYF-n. Như `MyFileReviseInput` (bản mới đã xin bằng
 * presign và đã PUT), nhưng bản cũ **được giữ** làm phiên bản trước. `.trim()` trước
 * `.max()`; ghi chú toàn khoảng trắng ⇒ không có ghi chú.
 */
export const MyFileVersionInput = z.object({
    pendingFileId: z.uuid(),
    note: z
        .string()
        .trim()
        .max(MY_FILE_VERSION_NOTE_MAX)
        .optional()
        .transform((value) => (value ? value : undefined)),
});
/** Một phiên bản trong "Các phiên bản" — APM-MYF-n. */
export const MyFileVersion = z.object({
    id: z.uuid(),
    versionNo: z.number().int().min(1),
    /** Lúc lưu phiên bản (ISO). */
    savedAt: z.iso.datetime(),
    note: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative(),
    /**
     * Bản đại diện — đứng đầu danh sách, đứng thay cả nhóm ở trang "Tệp", mở ra khi bấm đúp
     * (APM-VER-a). Không còn nghĩa "bản duy nhất sửa được": bản nào cũng sửa được.
     */
    representative: z.boolean(),
});
/**
 * Theo thứ tự người dùng xếp (kéo thả, APM-VER-a) — bản đại diện đầu tiên. Tệp chưa từng có
 * phiên bản ⇒ một mục (chính nó, số 1).
 */
export const MyFileVersions = z.object({ items: z.array(MyFileVersion) });
/** Số phiên bản tối đa một lần xếp lại — trần của mảng, không phải của nhóm trong thực tế. */
export const MY_FILE_VERSIONS_MAX = 200;
/**
 * Xếp lại thứ tự phiên bản — APM-VER-a. `ids` là **toàn bộ** phiên bản của nhóm theo thứ
 * tự mới; thiếu hay thừa một bản là 409 (danh sách người dùng thấy đã cũ). Bản đầu tiên
 * thành bản đại diện.
 */
export const MyFileVersionOrder = z.object({
    ids: z.array(z.uuid()).min(1).max(MY_FILE_VERSIONS_MAX),
});
/**
 * Số bóng sau biểu tượng của tệp có phiên bản — APM-MYF-n, người dùng chốt: một bóng
 * cho mỗi phiên bản trước, tối đa **2** (từ 3 phiên bản trở lên vẫn là 2 bóng).
 */
export function versionShadows(versions) {
    return Math.max(0, Math.min(2, Math.floor(versions) - 1));
}
export const MY_FILES_PAGE_MAX = 100;
export const MyFilesQuery = z.object({
    source: MyFileSource.optional(),
    /** Tìm theo tên, không phân biệt hoa thường lẫn dấu. `.trim()` trước `.min()` (CLAUDE.md). */
    q: z.string().trim().min(1).max(200).optional(),
    type: FileTypeGroup.optional(),
    /**
     * Thư mục tự tạo (plan §5.3), chỉ có nghĩa với tệp nguồn `mine`: uuid = tệp
     * trong thư mục đó, `'root'` = tệp `mine` chưa vào thư mục nào. Bỏ trống =
     * không lọc theo thư mục.
     */
    folderId: z.union([z.uuid(), z.literal('root')]).optional(),
    sort: z.enum(['name', 'createdAt', 'size']).default('createdAt'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    /** Chuỗi mờ lấy từ `nextCursor` của trang trước — gửi kèm đúng `sort`/`dir` cũ. */
    cursor: z.string().min(1).max(1000).optional(),
    limit: z.coerce.number().int().min(1).max(MY_FILES_PAGE_MAX).default(50),
});
export const MyFilesPage = z.object({
    items: z.array(MyFileItem),
    nextCursor: z.string().nullable(),
    /** Số mục mỗi thư mục ảo — **không** theo bộ lọc `source`/`q`/`type`. */
    counts: z.record(MyFileSource, z.number().int().nonnegative()),
    /** Tổng dung lượng các file đang hiện (mọi nguồn, không theo bộ lọc). */
    usedBytes: z.number().int().nonnegative(),
});
/** Tối đa 100 id — plan §2.5. */
export const MyFileUrlsInput = z.object({
    ids: z.array(z.uuid()).max(MY_FILES_PAGE_MAX),
});
export const MyFileUrlsResult = z.object({
    /** Id không đọc được (của người khác, đã xoá, không tồn tại) ⇒ `null`, không lỗi. */
    urls: z.record(z.string(), z.url().nullable()),
    expiresInSec: z.number().int().positive(),
});
/**
 * `type/subtype` theo RFC 6838 (không tham số): chữ, số và `!#$&^_.+-`.
 * Giá trị này nằm trong chữ ký presigned PUT, nên chỉ cần **đúng dạng** —
 * không cần nằm trong `ALLOWED_MIME_TYPES` (plan §2.3).
 */
const MIME_TYPE_SHAPE = /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/i;
/**
 * Tải lên một tệp cá nhân — APM-MYF-b, plan §2.3. **Mọi định dạng**: chỉ đường
 * này bỏ `AllowedMimeType`; file gắn vào dự án/to-do vẫn theo danh sách cho phép.
 */
export const MyFilePresignInput = z.object({
    /** `.trim()` trước `.min()` (CLAUDE.md). */
    filename: z.string().trim().min(1).max(255),
    /**
     * Giá trị trình duyệt nói (`File.type`). Rỗng/thiếu thì server suy từ đuôi —
     * xem `personalFileMimeType`. Có mà sai dạng ⇒ 400, không đoán thay.
     */
    mimeType: z
        .string()
        .trim()
        .max(255)
        .refine((value) => value === '' || MIME_TYPE_SHAPE.test(value), {
        message: 'mimeType phải có dạng type/subtype',
    })
        .optional(),
    sizeBytes: z.number().int().positive().max(MAX_FILE_BYTES),
    checksumSha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/, 'checksum phải là SHA-256 dạng hex thường, 64 ký tự'),
    /** Tải thẳng vào một thư mục tự tạo của mình (plan §5.3). Của người khác ⇒ 404. */
    folderId: z.uuid().optional(),
});
/**
 * MIME ghi cho một tệp cá nhân (plan §2.3): giá trị trình duyệt nói nếu có,
 * rỗng thì suy từ đuôi, vẫn không có thì `application/octet-stream`. Viết
 * thường — S3 và trình duyệt so không phân biệt hoa thường, còn cột
 * `mime_type` thì so đúng từng ký tự.
 */
export function personalFileMimeType(filename, mimeType) {
    const given = (mimeType ?? '').trim().toLowerCase();
    if (given !== '')
        return given;
    return mimeTypeForFilename(filename) ?? 'application/octet-stream';
}
/** Tài liệu `.md` cá nhân — sửa được ở trang Tệp (plan §2.4). */
export function isEditableMarkdownName(filename) {
    return filename.toLowerCase().endsWith('.md');
}
/**
 * Lưu bản mới của một tài liệu `.md` cá nhân — plan §2.4. `pendingFileId` là
 * một tệp đã xin bằng `POST /me/files/presign` và đã PUT lên S3, **chưa** commit.
 */
export const MyFileReviseInput = z.object({
    pendingFileId: z.uuid(),
});
/** Tối đa thư mục tự tạo mỗi người — plan §5.3. */
export const MY_FOLDERS_MAX = 200;
/**
 * Tên thư mục tự tạo — plan §5.3. `.trim()` trước `.min()` (CLAUDE.md); không
 * `/` `\\` vì người dùng đọc chúng như đường dẫn lồng nhau, mà thư mục chỉ một cấp.
 */
export const MyFileFolderName = z
    .string()
    .trim()
    .min(1, 'Tên thư mục không được trống')
    .max(100, 'Tên thư mục tối đa 100 ký tự')
    .refine((value) => !/[\\/]/.test(value), {
    message: 'Tên thư mục không được chứa / hoặc \\',
});
export const MyFileFolderInput = z.object({ name: MyFileFolderName });
export const MyFileFolder = z.object({
    id: z.uuid(),
    name: z.string(),
    /** Số tệp đang ở trong thư mục (chỉ tệp đã xác nhận). */
    fileCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
});
/** Chuyển tệp vào thư mục (`folderId`) hoặc về gốc (`null`) — tất cả hoặc không. */
export const MyFileMoveInput = z.object({
    fileIds: z.array(z.uuid()).min(1).max(MY_FILES_PAGE_MAX),
    folderId: z.uuid().nullable(),
});
/**
 * Đổi tên một tệp cá nhân ở trang Tệp — APM-VER-c (người dùng chuyển nút Đổi tên của hộp soạn
 * về đây, 2026-09-29). Tên **đầy đủ** kèm đuôi; đuôi phải giữ nguyên (đổi đuôi là đổi loại
 * tệp — trình soạn / trình xem sẽ mở sai). Tệp có phiên bản: cả nhóm đổi theo.
 */
export const MyFileRenameInput = z.object({
    name: z
        .string()
        .trim()
        .min(1)
        .max(255)
        .refine((value) => !/[\\/]/.test(value), { message: 'Tên tệp không được có dấu / hay \\' }),
});
/**
 * Trần nội dung một bản nháp — plan-my-files §5.4.
 *
 * Tính bằng **ký tự** nhưng giới hạn thật là body JSON 1 MB của API (bytes):
 * chữ Việt có dấu tốn 2–3 byte UTF-8, nên 300.000 ký tự ≈ 900 KB là mức cao
 * nhất vẫn lọt qua parser. Plan ban đầu ghi 500.000 — con số đó bị parser trả
 * 413 trước khi tới Zod.
 */
export const MY_DRAFT_MAX_CHARS = 300_000;
/** Tối đa bản nháp mỗi người — plan §5.4. */
export const MY_DRAFTS_MAX = 50;
const DraftContent = z
    .string()
    .max(MY_DRAFT_MAX_CHARS, 'Bản nháp quá dài để tự lưu — hãy lưu thành tệp');
/** Ghi đè nội dung nháp — loại tài liệu cố định từ lúc tạo. */
export const MyDraftInput = z.object({ content: DraftContent });
/** Tạo nháp — lần tự lưu đầu. `format` để mở lại đúng trình soạn (APM-MYF-l). */
export const MyDraftCreateInput = z.object({
    content: DraftContent,
    format: z.enum(DOCUMENT_FORMATS),
});
export const MyDraftSummary = z.object({
    id: z.uuid(),
    /** Dòng chữ đầu tiên (≤ 80 ký tự); `null` = nháp chưa có chữ nào. */
    title: z.string().nullable(),
    format: z.enum(DOCUMENT_FORMATS),
    updatedAt: z.iso.datetime(),
    chars: z.number().int().nonnegative(),
});
/**
 * Ảnh nhúng của một nháp `.chilldoc` — APM-CDIMG-b, docs/plan-chilldoc-images.md §4.1.
 * Nội dung nháp (≤ 1 MB qua API) giữ `chilldoc-image:{id}` mà không mang khối ảnh;
 * ảnh nằm tạm trên S3, trình duyệt tải về bằng `url` (presigned GET) khi mở lại nháp.
 */
export const MyDraftImage = z.object({
    imageId: z.string().regex(CHILLDOC_IMAGE_ID),
    mimeType: z.enum(CHILLDOC_IMAGE_TYPES),
    url: z.string(),
});
export const MyDraft = z.object({
    id: z.uuid(),
    content: z.string(),
    format: z.enum(DOCUMENT_FORMATS),
    updatedAt: z.iso.datetime(),
    /** Ảnh nhúng đã tải lên của nháp (APM-CDIMG-b); nháp không ảnh ⇒ `[]`. */
    images: z.array(MyDraftImage).default([]),
});
/** Trần số ảnh của một nháp — chặn một client lỗi rải object vô hạn lên bucket. */
export const MY_DRAFT_IMAGES_MAX = 200;
/** Xin URL tải lên một ảnh nhúng của nháp. `imageId` là băm nội dung do trình duyệt tính. */
export const MyDraftImagePresignInput = z.object({
    imageId: z.string().regex(CHILLDOC_IMAGE_ID),
    mimeType: z.enum(CHILLDOC_IMAGE_TYPES),
    sizeBytes: z.number().int().positive().max(CHILLDOC_MAX_IMAGE_BYTES),
});
export const MyDraftImageUpload = z.object({
    uploadUrl: z.string(),
    expiresInSec: z.number().int().positive(),
    requiredHeaders: z.record(z.string(), z.string()),
});
/**
 * Commit một tệp cá nhân — `fromDraftId`: tài liệu này sinh từ một bản nháp,
 * xoá nháp **cùng transaction** với commit (plan §5.4). Body có thể trống.
 */
export const MyFileCommitInput = z.object({ fromDraftId: z.uuid().optional() }).default({});
const DRAFT_TITLE_MAX = 80;
/**
 * Tiêu đề hiển thị của một bản nháp: dòng chữ đầu tiên. Nội dung có thể là
 * JSON `.chilldoc` (APM-CD-a) — lấy chữ của node đầu tiên có chữ — hoặc
 * markdown — bỏ ký hiệu đầu dòng (`#`, `>`, `-`, `1.`). Không bao giờ ném.
 */
export function draftTitleOf(content, format) {
    // Văn bản thuần: dòng đầu nguyên văn — `# ` hay `- ` ở đó là chữ của người viết.
    const text = format === 'text'
        ? (content.split('\n').find((line) => line.trim()) ?? null)
        : (chilldocFirstText(content) ?? markdownFirstLine(content));
    const trimmed = text?.replace(/\s+/g, ' ').trim();
    if (!trimmed)
        return null;
    return trimmed.length > DRAFT_TITLE_MAX ? `${trimmed.slice(0, DRAFT_TITLE_MAX - 1)}…` : trimmed;
}
function markdownFirstLine(content) {
    for (const line of content.split('\n')) {
        // Bỏ chú thích HTML (dòng lề của APM-MYF-h) và ký hiệu đầu dòng.
        const text = line
            .replace(/<!--.*?-->/g, '')
            .replace(/^\s*(#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/, '')
            .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
            .replace(/[*_`~]/g, '')
            .trim();
        if (text)
            return text;
    }
    return null;
}
function chilldocFirstText(content) {
    const read = readChilldoc(content);
    if (!read.ok)
        return null;
    // Duyệt theo khối: gom chữ của khối đầu tiên có chữ.
    const blocks = read.envelope.doc.content ?? [];
    for (const block of blocks) {
        const text = textOf(block);
        if (text.trim())
            return text;
    }
    return null;
}
function textOf(node) {
    if (!node || typeof node !== 'object')
        return '';
    const { text, content } = node;
    if (typeof text === 'string')
        return text;
    return Array.isArray(content) ? content.map(textOf).join('') : '';
}
//# sourceMappingURL=my-files.js.map